/**
 * Favicon por ambiente.
 *
 * Entre muchas pestañas abiertas, la de producción y la de desarrollo tienen que
 * distinguirse sin leer el título. Producción usa el ícono limpio
 * (`public/favicon.svg`); el resto, el mismo ícono con una insignia de color en la
 * esquina (`favicon-local.svg`, `favicon-staging.svg`).
 *
 * Solo se cambia el `<link rel="icon" type="image/svg+xml">`. Los PNG de respaldo
 * (navegadores sin favicon SVG, como Safari) muestran siempre el ícono limpio.
 */
import { classifyEnvironment } from '../lib/environment';

/** Variante del favicon según el ambiente. */
export type FaviconVariant = 'production' | 'staging' | 'local';

const FAVICON_FILES: Readonly<Record<FaviconVariant, string>> = {
  production: 'favicon.svg',
  staging: 'favicon-staging.svg',
  local: 'favicon-local.svg',
};

const SVG_ICON_SELECTOR = 'link[rel~="icon"][type="image/svg+xml"]';

/**
 * Variante del favicon para la etiqueta de `VITE_ENVIRONMENT_LABEL`, con la misma
 * lectura que la insignia del menú (`lib/environment.ts`): producción, un
 * servidor compartido (staging o el servidor de desarrollo) o cualquier otra
 * cosa, que se ve como local.
 *
 * Lo desconocido cae en `local` a propósito: una etiqueta mal escrita en
 * producción muestra la insignia y se nota, mientras que al revés un ambiente de
 * desarrollo se haría pasar por producción.
 */
export function resolveFaviconVariant(environmentLabel: string): FaviconVariant {
  const kind = classifyEnvironment(environmentLabel);
  if (kind === 'production' || kind === 'staging') {
    return kind;
  }
  return 'local';
}

/**
 * Apunta el favicon SVG a la variante del ambiente. Si `index.html` no trae el
 * `<link>`, lo crea al final de `<head>`, donde Chrome y Firefox lo prefieren a
 * los PNG de respaldo.
 */
export function applyEnvironmentFavicon(environmentLabel: string): void {
  const href = `${import.meta.env.BASE_URL}${FAVICON_FILES[resolveFaviconVariant(environmentLabel)]}`;
  const link =
    document.head.querySelector<HTMLLinkElement>(SVG_ICON_SELECTOR) ?? appendSvgIconLink();
  if (link.getAttribute('href') !== href) {
    link.setAttribute('href', href);
  }
}

function appendSvgIconLink(): HTMLLinkElement {
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/svg+xml';
  document.head.appendChild(link);
  return link;
}
